import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';
import { usePermissions } from '../../../utils/usePermissions';
import { notify } from '../../notifications';
import { ReportSecurityWrapper } from './ReportSecurityWrapper';
import { ChangeRow, ConfirmDialog, EditEmptyState, LimitChip, PharmacyAutocomplete, ScopeChip } from './edit/EditCommon';
import MonthGrid, { GridColumn } from './edit/MonthGrid';
import ReportHistoryButton from './history/ReportHistoryButton';
import {
  apiErrorMessage, EDIT_API, EditPharmacy, MonthResponse, SaveResponse, useEditAccess
} from './edit/editApi';
import {
  Calculator, createCalculator, DayValues, EditField, END_FIELD, findOverflow, formatDateRu,
  formatMonthRu, formatNumber, getCurrentMonthStr, getPrevMonthStr, groupFields, weekdayShort
} from './edit/editCalc';

const BORDER = '1px solid #e2e8f0';
const HISTORY_LIMIT = 100;
const MONTH_RE = /^\d{4}-\d{2}$/;

/** Правки месяца: значения всех дней и история для отмены/повтора (снимки делят неизмененные строки между собой) */
interface EditState {
  rows: DayValues[];
  past: DayValues[][];
  future: DayValues[][];
}

const EMPTY_STATE: EditState = { rows: [], past: [], future: [] };

interface DayChange {
  index: number;
  date: string;
  fields: EditField[];
}

/** Строки с одинаковыми значениями считаются одной и той же строкой (ссылка сохраняется, ячейки не перерисовываются) */
function sameValues(a: DayValues, b: DayValues): boolean {
  const keys = Object.keys(b);
  if (keys.length !== Object.keys(a).length) return false;
  for (let i = 0; i < keys.length; i++) {
    if (a[keys[i]] !== b[keys[i]]) return false;
  }
  return true;
}

/** Редактирование месячного отчета аптеки: таблица «дни x поля», пересчет остатков и итогов по цепочке, сохранение только измененных дней */
const EditMonthReport: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canProtectCopy = hasPerm('reports.edit_monthly.protect_copy');

  const { access, loading: accessLoading, error: accessError, reload: reloadAccess } = useEditAccess('monthly');

  const [pharmacy, setPharmacy] = useState<EditPharmacy | null>(null);
  const [month, setMonth] = useState<string>(getPrevMonthStr());
  const [data, setData] = useState<MonthResponse | null>(null);
  const [edit, setEdit] = useState<EditState>(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const requestId = useRef(0);
  const pendingChange = useRef<(() => void) | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<DayValues[]>([]);
  rowsRef.current = edit.rows;

  const currentMonth = useMemo(() => getCurrentMonthStr(), []);
  const monthLocked = Boolean(access && access.dateLimit === 'last_month');

  useEffect(() => {
    document.title = 'Редактировать месячный отчет | Zerde';
  }, []);

  // При ограничении «только прошлый месяц» месяц фиксируется на значении, которое вернул сервер
  useEffect(() => {
    if (access && access.dateLimit === 'last_month' && access.allowedMonth) {
      setMonth(access.allowedMonth);
    }
  }, [access]);

  const loadMonth = useCallback(async (username: string, ym: string, silent: boolean) => {
    const id = ++requestId.current;
    if (!silent) {
      setLoading(true);
      setLoadError('');
    }
    try {
      const res = await axios.get<MonthResponse>(`${EDIT_API}/month`, {
        params: silent ? { username, month: ym, silent: 1 } : { username, month: ym }
      });
      if (id !== requestId.current) return;
      if (!res.data || !res.data.success) {
        throw new Error((res.data as unknown as { message?: string })?.message || 'Не удалось загрузить отчет');
      }
      setData(res.data);
      setEdit({ rows: res.data.days.map((d) => d.values), past: [], future: [] });
    } catch (err) {
      if (id !== requestId.current) return;
      if (silent) {
        notify.warning('Данные сохранены, но обновить таблицу не удалось. Откройте отчет заново');
      } else {
        setData(null);
        setEdit(EMPTY_STATE);
        setLoadError(apiErrorMessage(err, 'Не удалось загрузить отчет'));
      }
    } finally {
      if (id === requestId.current && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!pharmacy || !MONTH_RE.test(month)) {
      requestId.current++;
      setData(null);
      setEdit(EMPTY_STATE);
      setLoadError('');
      setLoading(false);
      return;
    }
    loadMonth(pharmacy.username, month, false);
  }, [pharmacy, month, reloadKey, loadMonth]);

  const calcState = useMemo<{ calc: Calculator | null; error: string }>(() => {
    if (!data) return { calc: null, error: '' };
    try {
      return { calc: createCalculator(data.fields, data.computeOrder), error: '' };
    } catch (err) {
      return { calc: null, error: err instanceof Error ? err.message : 'Не удалось разобрать формулы отчета' };
    }
  }, [data]);

  const groups = useMemo(
    () => groupFields(data ? data.fields.filter((f) => f.kind !== 'meta') : []),
    [data]
  );
  const inputFields = useMemo(
    () => groups.reduce<EditField[]>((all, g) => all.concat(g.fields), []).filter((f) => f.kind === 'input'),
    [groups]
  );
  const columns = useMemo<GridColumn[]>(() => {
    let inputIndex = 0;
    return groups
      .reduce<EditField[]>((all, g) => all.concat(g.fields), [])
      .map((field) => ({
        field,
        inputIndex: field.kind === 'input' ? inputIndex++ : -1,
        strong: field.name === END_FIELD || field.important
      }));
  }, [groups]);

  const dayChanges = useMemo<DayChange[]>(() => {
    if (!data) return [];
    const out: DayChange[] = [];
    data.days.forEach((day, i) => {
      const row = edit.rows[i];
      if (day.future || !row) return;
      const fields = inputFields.filter((f) => row[f.name] !== day.values[f.name]);
      if (fields.length > 0) out.push({ index: i, date: day.date, fields });
    });
    return out;
  }, [data, edit.rows, inputFields]);

  const changedCells = dayChanges.reduce((sum, c) => sum + c.fields.length, 0);
  const dirty = dayChanges.length > 0;

  const overflow = useMemo(() => {
    if (!data) return null;
    for (let i = 0; i < data.days.length; i++) {
      if (data.days[i].future || !edit.rows[i]) continue;
      const field = findOverflow(data.fields, edit.rows[i]);
      if (field) return `${formatDateRu(data.days[i].date)}: поле «${field.label}»`;
    }
    return null;
  }, [data, edit.rows]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  /** Фиксация значения ячейки: пересчет остатка и формул с этого дня и до конца месяца */
  const handleCommit = useCallback((index: number, name: string, value: number) => {
    const calc = calcState.calc;
    if (!calc || !data) return;
    const startBalance = data.startBalance;

    setEdit((state) => {
      const current = state.rows[index];
      if (!current || current[name] === value) return state;

      // Цепочка пересчитывается целиком до конца месяца (как на сервере), но строки, значения которых
      // не изменились, остаются прежними объектами - таблица перерисует только то, что действительно поменялось
      const rows = state.rows.slice();
      for (let j = index; j < rows.length; j++) {
        const prevEnd = j === 0 ? startBalance : rows[j - 1][END_FIELD];
        const computed = calc.compute(j === index ? { ...rows[j], [name]: value } : rows[j], prevEnd);
        if (j === index || !sameValues(rows[j], computed)) rows[j] = computed;
      }
      return {
        rows,
        past: state.past.concat([state.rows]).slice(-HISTORY_LIMIT),
        future: []
      };
    });
  }, [calcState.calc, data]);

  const handleUndo = useCallback(() => {
    setEdit((s) => (s.past.length === 0 ? s : {
      rows: s.past[s.past.length - 1],
      past: s.past.slice(0, -1),
      future: s.future.concat([s.rows])
    }));
  }, []);

  const handleRedo = useCallback(() => {
    setEdit((s) => (s.future.length === 0 ? s : {
      rows: s.future[s.future.length - 1],
      past: s.past.concat([s.rows]),
      future: s.future.slice(0, -1)
    }));
  }, []);

  // Ctrl+Z / Ctrl+Y (по физическим клавишам, работает и в русской раскладке)
  useEffect(() => {
    if (!data) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const isUndo = e.code === 'KeyZ' && !e.shiftKey;
      const isRedo = e.code === 'KeyY' || (e.code === 'KeyZ' && e.shiftKey);
      if (!isUndo && !isRedo) return;

      const target = e.target as HTMLInputElement | null;
      if (target && target.tagName === 'INPUT') {
        // Внутри ячейки с набранным, но не зафиксированным текстом работает обычная отмена ввода
        const insideGrid = gridRef.current !== null && gridRef.current.contains(target);
        if (!insideGrid) return;
        const r = Number(target.getAttribute('data-r'));
        const c = Number(target.getAttribute('data-c'));
        const field = inputFields[c];
        const row = rowsRef.current[r];
        if (!field || !row || target.value !== String(row[field.name])) return;
        target.blur();
      }
      e.preventDefault();
      if (isUndo) handleUndo();
      else handleRedo();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [data, inputFields, handleUndo, handleRedo]);

  const guardedChange = (apply: () => void) => {
    if (dirty) {
      pendingChange.current = apply;
      setDiscardOpen(true);
    } else {
      apply();
    }
  };

  const handlePharmacyChange = (next: EditPharmacy | null) => guardedChange(() => setPharmacy(next));

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (monthLocked || !MONTH_RE.test(next) || next > currentMonth) return;
    guardedChange(() => setMonth(next));
  };

  const confirmDiscard = () => {
    const apply = pendingChange.current;
    pendingChange.current = null;
    setDiscardOpen(false);
    if (apply) apply();
  };

  const closeDiscard = () => {
    pendingChange.current = null;
    setDiscardOpen(false);
  };

  const handleReset = () => {
    if (!data) return;
    setEdit({ rows: data.days.map((d) => d.values), past: [], future: [] });
  };

  const handleSaveClick = () => {
    if (!dirty) {
      notify.info('Нет изменений для сохранения');
      return;
    }
    setConfirmOpen(true);
  };

  const handleSave = async () => {
    if (!data) return;
    const days = dayChanges.map((c) => {
      const values: DayValues = {};
      c.fields.forEach((f) => { values[f.name] = edit.rows[c.index][f.name]; });
      return { date: c.date, values };
    });

    setSaving(true);
    try {
      const res = await axios.put<SaveResponse>(`${EDIT_API}/month`, {
        username: data.pharmacy.username,
        month: data.month,
        days
      });
      if (!res.data || !res.data.success) {
        throw new Error((res.data && res.data.message) || 'Не удалось сохранить отчет');
      }
      notify.success(res.data.message || 'Данные сохранены');
      setConfirmOpen(false);

      // Сервер возвращает уже пересчитанный отчет месяца - отдельный запрос данных не нужен
      const report = res.data.report;
      if (report && report.success && report.month === data.month && report.pharmacy.username === data.pharmacy.username) {
        requestId.current++;
        setData(report);
        setEdit({ rows: report.days.map((d) => d.values), past: [], future: [] });
      } else {
        await loadMonth(data.pharmacy.username, data.month, true);
      }
    } catch (err) {
      setConfirmOpen(false);
      notify.error(apiErrorMessage(err, 'Не удалось сохранить отчет'));
    } finally {
      setSaving(false);
    }
  };

  const renderBody = () => {
    if (accessLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (accessError || !access) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось открыть редактирование"
          text={accessError || 'Нет данных о доступе'}
          action={<Button variant="outlined" onClick={reloadAccess} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>}
        />
      );
    }
    if (access.users.length === 0) {
      return (
        <EditEmptyState
          icon={<StorefrontOutlinedIcon />}
          title="Нет доступных аптек"
          text="В вашей области видимости нет аптек для редактирования отчетов."
        />
      );
    }
    if (!pharmacy) {
      return (
        <EditEmptyState
          icon={<StorefrontOutlinedIcon />}
          title="Выберите аптеку"
          text="После выбора аптеки и месяца откроется таблица отчета по дням."
        />
      );
    }
    if (!MONTH_RE.test(month)) {
      return <EditEmptyState icon={<ErrorOutlineIcon />} title="Укажите месяц отчета" />;
    }
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (loadError) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Отчет не загружен"
          text={loadError}
          action={
            <Button variant="outlined" onClick={() => setReloadKey((k) => k + 1)} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Повторить
            </Button>
          }
        />
      );
    }
    if (calcState.error) {
      return <EditEmptyState icon={<ErrorOutlineIcon />} title="Ошибка в формулах отчета" text={calcState.error} />;
    }
    if (!data || edit.rows.length !== data.days.length) return null;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem' }}>
            Аптека {data.pharmacy.pharmacyNum} &middot; {formatMonthRu(data.month)}
          </Typography>
          <Typography sx={{ fontSize: '0.84rem', color: '#64748b' }}>
            {data.startBalance === null
              ? 'Более ранних записей нет, остаток на начало месяца задается вручную'
              : `Остаток в кассе на начало месяца: ${formatNumber(data.startBalance)}`}
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden' }}>
          <MonthGrid
            days={data.days}
            rows={edit.rows}
            groups={groups}
            columns={columns}
            disabled={saving}
            gridRef={gridRef}
            onCommit={handleCommit}
          />
        </Paper>

        {/* Панель действий */}
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            bottom: 8,
            zIndex: 5,
            mt: 2,
            px: 2,
            py: 1.25,
            border: BORDER,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)'
          }}
        >
          <Typography sx={{ fontSize: '0.86rem', color: dirty ? '#92400e' : '#64748b', fontWeight: dirty ? 600 : 400 }}>
            {overflow
              ? `${overflow}: результат выходит за допустимый диапазон`
              : dirty
                ? `Изменено ячеек: ${changedCells}, дней: ${dayChanges.length}`
                : 'Изменений нет'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={handleReset}
            disabled={!dirty || saving}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}
          >
            Сбросить
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveClick}
            disabled={saving || Boolean(overflow)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', minWidth: 190 }}
          >
            Сохранить и пересчитать
          </Button>
        </Paper>
      </Box>
    );
  };

  return (
    <ReportSecurityWrapper protectCopy={canProtectCopy} username={pharmacy ? pharmacy.username : undefined}>
      <Box>
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, border: BORDER, borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}
        >
          <PharmacyAutocomplete
            options={access ? access.users : []}
            value={pharmacy}
            onChange={handlePharmacyChange}
            loading={accessLoading}
            disabled={saving}
          />
          <Tooltip title={monthLocked ? 'Политика роли: доступен только прошлый месяц' : ''} arrow>
            <span>
              <TextField
                size="small"
                label="Месяц отчета"
                type="month"
                value={month}
                onChange={handleMonthChange}
                disabled={monthLocked || saving}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: currentMonth }}
                InputProps={{
                  endAdornment: monthLocked ? (
                    <InputAdornment position="end">
                      <LockIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    </InputAdornment>
                  ) : undefined
                }}
                sx={{ minWidth: 180 }}
              />
            </span>
          </Tooltip>
          {data && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Отменить (Ctrl+Z)">
                <span>
                  <IconButton onClick={handleUndo} disabled={edit.past.length === 0 || saving} aria-label="Отменить" size="small">
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Повторить (Ctrl+Y)">
                <span>
                  <IconButton onClick={handleRedo} disabled={edit.future.length === 0 || saving} aria-label="Повторить" size="small">
                    <RedoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <ReportHistoryButton screen="edit_month" pharmacy={pharmacy ? pharmacy.username : null} period={month} />
          {access && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LimitChip limit={access.dateLimit} />
              <ScopeChip scope={access.scope} />
            </Box>
          )}
        </Paper>

        {renderBody()}
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Сохранить изменения"
        confirmText="Сохранить"
        loading={saving}
        maxWidth="md"
        onConfirm={handleSave}
        onClose={() => setConfirmOpen(false)}
      >
        {data && (
          <>
            <Typography sx={{ fontSize: '0.88rem', color: '#475569', mb: 1.5 }}>
              Аптека {data.pharmacy.pharmacyNum}, {formatMonthRu(data.month)}. Изменено дней: {dayChanges.length}.
              Остатки и формулы следующих дней будут пересчитаны автоматически.
            </Typography>
            <Box sx={{ maxHeight: 360, overflowY: 'auto', border: BORDER, borderRadius: '8px' }}>
              {dayChanges.map((c) => (
                <Box key={c.date} sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9', '&:last-of-type': { borderBottom: 'none' } }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', mb: 0.25 }}>
                    {formatDateRu(c.date)}, {weekdayShort(c.date)}
                    {data.days[c.index].exists ? '' : ' (новая запись)'}
                  </Typography>
                  {c.fields.map((f) => (
                    <ChangeRow
                      key={f.name}
                      label={f.label}
                      from={formatNumber(data.days[c.index].values[f.name], f)}
                      to={formatNumber(edit.rows[c.index][f.name], f)}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={discardOpen}
        title="Несохраненные изменения"
        confirmText="Отменить изменения"
        cancelText="Продолжить редактирование"
        maxWidth="xs"
        onConfirm={confirmDiscard}
        onClose={closeDiscard}
      >
        <Typography sx={{ fontSize: '0.9rem', color: '#475569' }}>
          В отчете есть несохраненные изменения. Если продолжить, они будут потеряны.
        </Typography>
      </ConfirmDialog>
    </ReportSecurityWrapper>
  );
};

export default EditMonthReport;
